import { useCallback, useMemo } from 'react'
import { getIn } from '../../Formik/forms'
import { useHostname } from 'lib/HostnameProvider'
import { useOrbit } from 'providers/OrbitProvider'
import { ClientLevelExclusionRules } from './ClientLevelExclusionRules'
import { ProgramLevelExclusionRules } from './ProgramLevelExclusionRules'
import { FormPostingSetup } from './FormPostingSetup'
import { RequestParameters } from './RequestParameters'
import { InitializedRecord } from '@orbit/records'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import { useExportRegistry } from './ExportRegistryProvider'
import { useNotifications } from 'lib/NotificationsProvider'
import { exportAllSheetsToWorkbook } from './exportHelper'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';

export function ExportFpiButton({ clientName }) {
  const { addNotification } = useNotifications()
  const registry = useExportRegistry()

  const onExportXlsx = useCallback(async () => {
    const sheets = registry.list()
    if (!sheets.length) {
      addNotification?.({ variant: 'alert', message: 'No tables found to export.' })
      return
    }

    try {
      await exportAllSheetsToWorkbook(sheets, `[${clientName}] Form Posting Instructions.xlsx`)
      addNotification?.({ variant: 'notice', message: 'Export created.' })
    } catch {
      addNotification?.({ variant: 'alert', message: 'Error creating export.' })
    }
  }, [registry, addNotification, clientName])

  return (
    <Tooltip title='Export Form Posting Instructions'>
      <IconButton onClick={onExportXlsx}>
        <DownloadIcon />
      </IconButton>
    </Tooltip>
  )
}

export default function Integrations({ clientCampaignsResource, contractResource, fieldsResource, degreeProgramsResource, clientExclusionRulesResource }) {
  const { store } = useOrbit()
  const contract = contractResource.get()
  const fields = fieldsResource.get()
  const clientCampaigns = clientCampaignsResource.get()
  const degreePrograms = degreeProgramsResource.get()
  const clientExclusionRules = clientExclusionRulesResource.get()

  const client = useMemo(() => store.cache.query(q => q.findRelatedRecord(contract, 'client')) as InitializedRecord, [contract, store])
  const leadsEndpoint = useMemo(() => client && store.cache.query(q => q.findRelatedRecord(client, 'leadsEndpoint')), [client, store])
  const { defaultLeadsHostname } = useHostname()

  const leadsHostname = getIn(leadsEndpoint, 'attributes.hostname') || defaultLeadsHostname
  const leadsPath = getIn(leadsEndpoint, 'attributes.path') || '/leads'
  const url = `https://${leadsHostname}${leadsPath}`

  const clientTimezone = getIn(client, 'attributes.timeZone') || 'UTC'

  return (
    <Box sx={{ width: '100%' }}>
      <FormPostingSetup url={url} />
      <RequestParameters clientCampaigns={clientCampaigns} fields={fields} degreePrograms={degreePrograms} leadsEndpoint={leadsEndpoint} />
      <ClientLevelExclusionRules clientExclusionRules={clientExclusionRules} clientTimezone={clientTimezone} />
      <ProgramLevelExclusionRules degreePrograms={degreePrograms} clientTimezone={clientTimezone} />
    </Box>
  )
}
